import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation } from "../context/LocationContext";
import { BsGeoAlt, BsSearch, BsX, BsArrowRight } from "react-icons/bs";

// Debounce utility
const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const HomeSearchBar = () => {
    const [locationQuery, setLocationQuery] = useState("");
    const [locationResults, setLocationResults] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null); 
    const [searchQuery, setSearchQuery] = useState("");
    const [specializationResults, setSpecializationResults] = useState([]);
    const [doctorResults, setDoctorResults] = useState([]);
    const [hospitalResults, setHospitalResults] = useState([]);
    const { setLocation } = useLocation();

    // 🔗 Redirect helper (Unified Logic)
    const redirectToProfiles = ({ type, id, name }) => {
        const url = new URL("https://app.gkcklinica.com/public-profiles");
        url.searchParams.set("locality_id", selectedLocation?.type === "LOCALITY" ? selectedLocation?.id : "");
        url.searchParams.set("city_id", selectedLocation?.type === "CITY" ? selectedLocation?.id : "");
        url.searchParams.set("specialization_id", type === "specialization" ? id : "");
        url.searchParams.set("search_query", name || searchQuery || "");
        url.searchParams.set("search_type", type);
        window.location.href = url.toString();
    };

    // Update Context Location
    useEffect(() => {
        if (selectedLocation) {
            setLocation({
                localityId: selectedLocation.type === "LOCALITY" ? selectedLocation.id : "",
                cityId: selectedLocation.type === "CITY" ? selectedLocation.id : "",
                type: selectedLocation.type,
                name: selectedLocation.name,
            });
        }
    }, [selectedLocation, setLocation]);

    // Fetch Locations logic
    const fetchLocations = async (query) => {
        if (!query) return setLocationResults([]);
        try {
            const res = await axios.get(`https://api.gkcklinica.com/user/localitiesCities?limit=5&name=${query}`);
            const merged = [...(res.data?.allCities || []), ...(res.data?.allLocalities || [])];
            setLocationResults(merged);
        } catch (error) { console.error("Location fetch error", error); }
    };

    const debouncedLocationSearch = useCallback(debounce(fetchLocations, 400), []);
    useEffect(() => { debouncedLocationSearch(locationQuery); }, [locationQuery]);

    // Fetch Specializations logic
    const fetchSpecializations = async (query) => {
        if (!query) return setSpecializationResults([]);
        try {
            const res = await axios.get(`https://api.gkcklinica.com/user/specializations?name=${query}`);
            setSpecializationResults(res.data?.data || res.data || []);
        } catch (error) { console.error("Specialization fetch error", error); }
    };

    const debouncedSpecializationSearch = useCallback(debounce(fetchSpecializations, 400), []);
    useEffect(() => { 
        debouncedSpecializationSearch(searchQuery);
        setDoctorResults([]);
        setHospitalResults([]);
    }, [searchQuery]);

    // Default Search Trigger (For the "Find Care" Button)
    const handleMainAction = () => {
        if (!selectedLocation?.id) {
            toast.warn("Please select a location first");
            return;
        }
        if (!searchQuery) {
            toast.info("Please enter a doctor name or specialization");
            return;
        }
        redirectToProfiles({ type: "doctors", name: searchQuery });
    };

    return (
        <div className="gk_portal_engine">
            <h2 className="gk_portal_heading">Consult with Top Doctors Online, 24x7</h2>
            <div className="gk_portal_bar_frame">
                
                {/* 🔹 PART 1: LOCATION SEARCH */}
                <div className="gk_portal_field_box geo_zone">
                    <div className="gk_portal_input_core">
                        <BsGeoAlt className="gk_portal_icon" />
                        <input
                            type="text"
                            placeholder="Enter location"
                            value={locationQuery}
                            onChange={(e) => setLocationQuery(e.target.value)}
                        />
                        {locationQuery && <BsX className="gk_portal_clear" onClick={() => setLocationQuery("")} />}
                    </div>

                    {locationResults.length > 0 && (
                        <div className="gk_portal_dropdown visible">
                            <label className="gk_portal_drop_label">Recent Search</label>
                            <ul className="gk_portal_drop_list">
                                {locationResults.map((loc, idx) => (
                                    <li key={idx} onClick={() => {
                                        const name = loc.name || loc.city || loc.locality;
                                        setLocationQuery(name);
                                        setSelectedLocation({ 
                                            id: loc.id, 
                                            name, 
                                            type: loc.districtId ? "CITY" : "LOCALITY" 
                                        });
                                        setLocationResults([]);
                                    }}>
                                        <div className="gk_portal_loc_meta">
                                            <span className="gk_portal_main_text">{loc.name || loc.city || loc.locality}</span>
                                            <span className="gk_portal_sub_tag">{loc.districtId ? "CITY" : loc?.cities?.name || "LOCALITY"}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="gk_portal_divider_rail d-none d-lg-block"></div>

                {/* 🔹 PART 2: MEDICAL CONTENT SEARCH */}
                <div className="gk_portal_field_box medical_zone">
                    <div className="gk_portal_input_core">
                        <BsSearch className="gk_portal_icon" />
                        <input
                            type="text"
                            placeholder="Search Doctor, Hospital, or Specialization"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleMainAction()}
                        />
                    </div>

                    {(specializationResults.length > 0 || searchQuery.length > 0) && (
                        <div className="gk_portal_dropdown visible">
                            <label className="gk_portal_drop_label">Suggestions</label>
                            <ul className="gk_portal_drop_list">
                                {/* Static Search Options */}
                                <li className="gk_portal_direct_hit" onClick={() => redirectToProfiles({ type: "doctors", name: searchQuery })}>
                                    Search for <strong>"{searchQuery}"</strong> by Doctor <BsArrowRight />
                                </li>
                                <li className="gk_portal_direct_hit" onClick={() => redirectToProfiles({ type: "hospitals", name: searchQuery })}>
                                    Search for <strong>"{searchQuery}"</strong> by Hospital <BsArrowRight />
                                </li>

                                {/* Dynamic Specialization Results */}
                                {specializationResults.slice(0, 5).map((spec, idx) => (
                                    <li key={idx} onClick={() => redirectToProfiles({ type: "specialization", id: spec.id, name: spec.name })}>
                                        <span className="gk_portal_main_text">{spec.name}</span>
                                        <span className="gk_portal_sub_tag">Specialization</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* 🔹 PART 3: ACTION BUTTON */}
                <button className="gk_portal_trigger_btn" onClick={handleMainAction}>
                    <BsSearch className="d-lg-none" />
                    <span className="d-none d-lg-inline">Find Care</span>
                </button>

            </div>
        </div>
    );
};

export default HomeSearchBar;